import React, { useState, useEffect } from 'react';
import { apiCall } from '../../api';
import TokenDisplayModal from './TokenDisplayModal';

const TokensTab = () => {
  const [tokens, setTokens] = useState([]);
  const [apiUsers, setApiUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showTokenDisplay, setShowTokenDisplay] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [editingToken, setEditingToken] = useState(null);
  const [formData, setFormData] = useState({
    api_user_id: '',
    name: '',
    description: '',
    expires_in_days: '',
    rate_limit: 1000,
    permissions: {
      read: false,
      create: false,
      update: false,
      delete: false,
      manage_users: false
    }
  });

  useEffect(() => {
    fetchTokens();
    fetchApiUsers();
  }, []);

  const fetchTokens = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/tokens');
      setTokens(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching tokens:', err);
      setError('Failed to load tokens');
    } finally {
      setLoading(false);
    }
  };

  const fetchApiUsers = async () => {
    try {
      const users = await apiCall('/api-users');
      setApiUsers(users.filter(u => u.status === 'active'));
    } catch (err) {
      console.error('Error fetching API users:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingToken) {
        const updateData = {
          name: formData.name,
          description: formData.description,
          rate_limit: formData.rate_limit,
          permissions: formData.permissions
        };
        await apiCall(`/tokens/${editingToken.token_id}`, {
          method: 'PUT',
          body: JSON.stringify(updateData)
        });
        setShowModal(false);
        setEditingToken(null);
        fetchTokens();
      } else {
        // Convert and clean data before sending
        const submitData = {
          api_user_id: parseInt(formData.api_user_id),
          name: formData.name,
          description: formData.description || null,
          expires_in_days: formData.expires_in_days ? parseInt(formData.expires_in_days) : null,
          rate_limit: formData.rate_limit,
          permissions: formData.permissions
        };
        const result = await apiCall('/tokens', {
          method: 'POST',
          body: JSON.stringify(submitData)
        });
        setNewToken(result);
        setShowModal(false);
        setShowTokenDisplay(true);
        fetchTokens();
      }
      resetForm();
    } catch (err) {
      alert(err.message || 'Failed to save token');
    }
  };

  const handleDelete = async (tokenId) => {
    if (!window.confirm('Are you sure you want to revoke this token? This action cannot be undone.')) {
      return;
    }
    try {
      await apiCall(`/tokens/${tokenId}`, { method: 'DELETE' });
      fetchTokens();
    } catch (err) {
      alert(err.message || 'Failed to delete token');
    }
  };

  const handleResetRateLimit = async (tokenId) => {
    try {
      await apiCall(`/tokens/${tokenId}/reset`, { method: 'POST' });
      fetchTokens();
      alert('Rate limit reset successfully');
    } catch (err) {
      alert(err.message || 'Failed to reset rate limit');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setEditingToken(null);
    setShowModal(true);
  };

  const openEditModal = (token) => {
    setFormData({
      api_user_id: token.api_user_id,
      name: token.name,
      description: token.description || '',
      expires_in_days: '',
      rate_limit: token.rate_limit,
      permissions: token.permissions
    });
    setEditingToken(token);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      api_user_id: apiUsers.length > 0 ? apiUsers[0].api_user_id : '',
      name: '',
      description: '',
      expires_in_days: '',
      rate_limit: 1000,
      permissions: {
        read: false,
        create: false,
        update: false,
        delete: false,
        manage_users: false
      }
    });
  };

  const togglePermission = (permission) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permission]: !formData.permissions[permission]
      }
    });
  };

  const getPermissionBadges = (permissions) => {
    const permColors = {
      read: 'bg-blue-100 text-blue-800',
      create: 'bg-green-100 text-green-800',
      update: 'bg-yellow-100 text-yellow-800',
      delete: 'bg-red-100 text-red-800',
      manage_users: 'bg-purple-100 text-purple-800'
    };

    return Object.entries(permissions)
      .filter(([, value]) => value)
      .map(([key]) => (
        <span key={key} className={`px-2 py-1 text-xs font-semibold rounded mr-1 ${permColors[key]}`}>
          {key}
        </span>
      ));
  };

  if (loading) {
    return <div className="text-center py-8">Loading tokens...</div>;
  }

  if (error) {
    return <div className="bg-red-50 border border-red-200 rounded p-4 text-red-800">{error}</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">API Tokens</h3>
        <button
          onClick={openCreateModal}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition duration-200"
          disabled={apiUsers.length === 0}
        >
          + Generate Token
        </button>
      </div>

      {apiUsers.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-4">
          <p className="text-yellow-800">Please create an API user first before generating tokens.</p>
        </div>
      )}

      {tokens.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded p-8 text-center">
          <p className="text-gray-600 mb-4">No tokens found</p>
          {apiUsers.length > 0 && (
            <button
              onClick={openCreateModal}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition duration-200"
            >
              Generate Your First Token
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prefix</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permissions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate Limit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tokens.map((token) => (
                <tr key={token.token_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{token.name}</div>
                    <div className="text-xs text-gray-500">Last used: {token.last_used_at ? new Date(token.last_used_at).toLocaleString() : 'Never'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {token.api_user_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">{token.token_prefix}...</code>
                  </td>
                  <td className="px-6 py-4">
                    {getPermissionBadges(token.permissions)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>{token.request_count} / {token.rate_limit}</div>
                    {token.request_count >= token.rate_limit && (
                      <button
                        onClick={() => handleResetRateLimit(token.token_id)}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Reset
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {token.is_active ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">Active</span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">Inactive</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => openEditModal(token)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(token.token_id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">
              {editingToken ? 'Edit Token' : 'Generate New Token'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 mb-4">
                {!editingToken && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API User *
                    </label>
                    <select
                      required
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      value={formData.api_user_id}
                      onChange={(e) => setFormData({ ...formData, api_user_id: e.target.value })}
                    >
                      <option value="">Select API User</option>
                      {apiUsers.map((user) => (
                        <option key={user.api_user_id} value={user.api_user_id}>
                          {user.name} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Token Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Production API Token"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    rows="2"
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {!editingToken && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expires In (days)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="365"
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        value={formData.expires_in_days}
                        onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                        placeholder="Leave empty for no expiration"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rate Limit (requests/hour)
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      max="10000"
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      value={formData.rate_limit}
                      onChange={(e) => setFormData({ ...formData, rate_limit: parseInt(e.target.value) })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permissions * (at least one required)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.keys(formData.permissions).map((perm) => (
                      <label key={perm} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.permissions[perm]}
                          onChange={() => togglePermission(perm)}
                          className="rounded"
                        />
                        <span className="text-sm capitalize">{perm.replace('_', ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingToken ? 'Update Token' : 'Generate Token'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Token Display Modal */}
      {showTokenDisplay && newToken && (
        <TokenDisplayModal
          token={newToken.token}
          tokenData={newToken}
          onClose={() => {
            setShowTokenDisplay(false);
            setNewToken(null);
          }}
        />
      )}
    </div>
  );
};

export default TokensTab;
